export type BreakEvenDecisionInput = Readonly<{
  fixedCost: number;
  unitPrice: number;
  unitVariableCost: number;
  targetProfit?: number;
  currentUnits?: number;
}>;

export type BreakEvenDecisionResult = Readonly<{
  fixedCost: number;
  unitPrice: number;
  unitVariableCost: number;
  unitContribution: number;
  contributionMarginRate: number;
  breakEvenUnits: number | null;
  breakEvenRevenue: number | null;
  targetProfit: number;
  targetUnits: number | null;
  targetRevenue: number | null;
  currentUnits: number | null;
  currentRevenue: number | null;
  currentProfit: number | null;
  safetyUnits: number | null;
  safetyRate: number | null;
}>;

export type BreakEvenScenario = Readonly<{
  id: string;
  label: string;
  description: string;
  result: BreakEvenDecisionResult;
}>;

function optionalFinite(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function calculateBreakEvenDecision(input: BreakEvenDecisionInput): BreakEvenDecisionResult {
  const unitContribution = input.unitPrice - input.unitVariableCost;
  const contributionMarginRate = input.unitPrice === 0
    ? 0
    : (unitContribution / input.unitPrice) * 100;
  const targetProfit = Math.max(0, optionalFinite(input.targetProfit) ?? 0);
  const currentUnits = optionalFinite(input.currentUnits);

  if (unitContribution <= 0) {
    const currentRevenue = currentUnits === null ? null : currentUnits * input.unitPrice;
    const currentProfit = currentUnits === null
      ? null
      : currentUnits * unitContribution - input.fixedCost;

    return {
      ...input,
      targetProfit,
      currentUnits,
      unitContribution,
      contributionMarginRate,
      breakEvenUnits: null,
      breakEvenRevenue: null,
      targetUnits: null,
      targetRevenue: null,
      currentRevenue,
      currentProfit,
      safetyUnits: null,
      safetyRate: null,
    };
  }

  const breakEvenUnits = Math.ceil(input.fixedCost / unitContribution);
  const breakEvenRevenue = breakEvenUnits * input.unitPrice;
  const targetUnits = Math.ceil((input.fixedCost + targetProfit) / unitContribution);
  const targetRevenue = targetUnits * input.unitPrice;
  const currentRevenue = currentUnits === null ? null : currentUnits * input.unitPrice;
  const currentProfit = currentUnits === null
    ? null
    : currentUnits * unitContribution - input.fixedCost;
  const safetyUnits = currentUnits === null ? null : currentUnits - breakEvenUnits;
  const safetyRate = currentUnits === null || currentUnits <= 0
    ? null
    : ((currentUnits - breakEvenUnits) / currentUnits) * 100;

  return {
    ...input,
    targetProfit,
    currentUnits,
    unitContribution,
    contributionMarginRate,
    breakEvenUnits,
    breakEvenRevenue,
    targetUnits,
    targetRevenue,
    currentRevenue,
    currentProfit,
    safetyUnits,
    safetyRate,
  };
}

export function buildBreakEvenScenarios(input: BreakEvenDecisionInput): readonly BreakEvenScenario[] {
  const scenarios: Array<{ id: string; label: string; description: string; input: BreakEvenDecisionInput }> = [
    {
      id: 'current',
      label: '현재 조건',
      description: '입력한 고정비·판매단가·변동비 기준입니다.',
      input,
    },
    {
      id: 'price-down-10',
      label: '판매단가 10% 인하',
      description: '판매단가가 10% 낮아졌을 때 필요한 손익분기 판매량을 비교합니다.',
      input: { ...input, unitPrice: input.unitPrice * 0.9 },
    },
    {
      id: 'variable-cost-up-10',
      label: '변동비 10% 상승',
      description: '단위당 변동비가 10% 늘어났을 때의 영향을 비교합니다.',
      input: { ...input, unitVariableCost: input.unitVariableCost * 1.1 },
    },
    {
      id: 'fixed-cost-up-10',
      label: '고정비 10% 상승',
      description: '월 고정비가 10% 늘어났을 때 필요한 판매량과 매출을 비교합니다.',
      input: { ...input, fixedCost: input.fixedCost * 1.1 },
    },
  ];

  return scenarios.map((scenario) => ({
    id: scenario.id,
    label: scenario.label,
    description: scenario.description,
    result: calculateBreakEvenDecision(scenario.input),
  }));
}

export function describeBreakEvenDecision(result: BreakEvenDecisionResult): string {
  if (result.unitContribution <= 0 || result.breakEvenUnits === null) {
    return '판매단가가 단위당 변동비 이하라 판매할수록 손실이 커집니다. 가격 또는 변동비 구조를 먼저 조정해야 손익분기점을 만들 수 있습니다.';
  }

  if (result.currentUnits !== null && result.currentProfit !== null) {
    if (result.currentProfit > 0) {
      return `현재 판매량 기준으로 고정비를 회수한 뒤 이익이 발생하는 구조입니다. 손익분기점보다 ${Math.max(0, result.currentUnits - result.breakEvenUnits).toLocaleString('ko-KR')}개 더 판매하고 있습니다.`;
    }
    if (result.currentProfit < 0) {
      return `현재 판매량 기준으로 아직 고정비를 모두 회수하지 못했습니다. 손익분기점까지 ${Math.max(0, result.breakEvenUnits - result.currentUnits).toLocaleString('ko-KR')}개가 더 필요합니다.`;
    }
    return '현재 판매량이 손익분기점과 일치해 영업이익이 0원인 상태입니다.';
  }

  return `한 개 판매할 때 고정비 회수에 기여하는 금액은 ${Math.round(result.unitContribution).toLocaleString('ko-KR')}원이며, 손익분기점은 약 ${result.breakEvenUnits.toLocaleString('ko-KR')}개입니다.`;
}
